import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { message, Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { Deployment } from '@/models/deployment.ts'
import { SliderInput } from '@/pages/instance-list/components/SliderInput'

interface EditTtlModalProps {
  open: boolean
  onClose: () => void
  deployment: Deployment
  enabled?: boolean
  onUpdate?: () => void
}

export default function EditTtlModal({
  open,
  onClose,
  deployment,
  enabled,
  onUpdate,
}: EditTtlModalProps) {
  const { t } = useTranslation('autoDeploy')
  const [ttlValue, setTtlValue] = useState(0) // in minutes
  const [updating, setUpdating] = useState(false)

  // Initialize modal values when opening
  useEffect(() => {
    if (open) {
      setTtlValue((deployment.ttlSeconds || 0) / 60)
    }
  }, [open, deployment.ttlSeconds])

  // Handle TTL update
  const handleUpdate = useCallback(() => {
    setUpdating(true)
    API.updateDeployment({
      res_id: deployment.resID,
      gtw_res_id: deployment.gateway.resID,
      enabled: enabled ?? true,
      description: deployment.description,
      hardware_resource: JSON.stringify(deployment.runtimeEnv.hardwareEnv.resource),
      name: deployment.name,
      port: deployment.gateway.port,
      system_env: JSON.stringify(deployment.runtimeEnv.systemEnv),
      ttl_seconds: ttlValue * 60, // Convert minutes to seconds
      max_replicas: deployment.maxReplicas, // Keep original value
    })
      .then((res) => {
        if (res.code === 0) {
          message.success(t('common.updateSuccess'))
          onClose()
          onUpdate?.()
        }
      })
      .catch((error) => {
        console.error(error)
        message.error(t('common.updateFailed'))
      })
      .finally(() => {
        setUpdating(false)
      })
  }, [deployment, enabled, ttlValue, t, onClose, onUpdate])

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>
        <Trans i18nKey="deployTarget.editTtl.title" ns="autoDeploy">
          Edit Instance Idle Retention Period
        </Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="t-sb mb-2">
          <Trans i18nKey="createSubPage.form.ttlSeconds" ns="autoDeploy">
            Instance Idle Retention Period
          </Trans>
        </div>
        <SliderInput
          value={ttlValue}
          min={3}
          max={90}
          unit={t('createSubPage.form.ttlSecondsUnit')}
          step={1}
          onChange={setTtlValue}
          showReset={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} className="min-w-[88px]">
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          onClick={handleUpdate}
          className="min-w-[88px]"
          disabled={updating}
        >
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
